import { actions, containers, listsV2 } from '@ecosystem/newron-design-system';
import {
  ContextSelectorContainerDropdownProps,
  ContextSelectorContainerModalProps,
} from '@ecosystem/newron-design-system/build/components/contextSelectorContainer/types';
import { BackgroundColor } from '@ecosystem/newron-design-system/build/exports/hiddenList';
import React, { useCallback, useEffect, useState } from 'react';

import {
  ContextSelectorType,
  RestrictedColData,
  RestrictedColumn,
  SelectedRows,
  Tab,
  TableInfo,
  TableRow,
} from '../types';
import { getColumnValue } from '../utils';
import { ContextBody } from './ContextBody';
import { ContextNavigation } from './ContextNavigation';
import { SelectionBody } from './SelectionBody';
import { SelectionFooter } from './SelectionFooter';

const { ContextSelectorContainer } = containers;
const { Button } = actions;

/**
 * Props for the ContextSelector component.
 */
export type ContextSelectorProps = {
  /**
   * The table information including data and column configuration.
   */
  tableInfo: TableInfo;
  /**
   * Callback function to handle viewing selected rows.
   * @param selectedRows - The rows that are currently selected.
   */
  onView: (selectedRows: TableRow[]) => unknown;
  /**
   * The text displayed on the trigger button or dropdown that opens the context selector.
   * @default 'Open context selector'
   */
  triggerText?: string;
  /**
   * Determines the display mode of the context selector.
   * - 'Dropdown': Renders as a dropdown.
   * - 'Modal': Renders as a modal dialog.
   * @default 'modal'
   */
  type?: ContextSelectorType;
  /**
   * The tabs to be displayed in the context selector.
   * Each tab should have a unique value and a label.
   * @default [{ value: 'default', label: 'All' }]
   */
  tabs?: Tab[];
  /**
   * The type of row selection to be used in the table.
   * - 'single': Single row selection.
   * - 'multiple': Multiple row selection.
   * @default 'multiple'
   */
  rowSelectionMode?: listsV2.RowSelectionMode;
  /**
   * The header for the selection section in the context selector.
   * @default 'Selected items'
   */
  selectionSectionHeader?: string;
  /**
   * An object representing the initial selected rows.
   * The keys are row IDs and the values are booleans indicating selection status.
   * @example { 'id1': true, 'id2': true }
   */
  initialSelectedRows?: SelectedRows;
  /**
   * Represents the configuration for column for which you can select only a single value.
   * When a value from this column is selected, a warning alert will be shown at the bottom of the context selector body.
   * This is useful for scenarios where selecting multiple values from a specific column could lead to issues or conflicts.
   */
  restrictedColumn?: RestrictedColumn;
};

const DEFAULT_TAB: Tab = {
  value: 'default',
  label: 'All',
  searchPlaceHolder: 'Search',
};

const isRestrictedColValueInvalid = (
  currentColValue: string,
  oldColValue: string,
  restrictedValues: string[] = []
): boolean => {
  if (!oldColValue || currentColValue === oldColValue) return false;
  if (!restrictedValues.length) return true;

  const currentColValRestricted = restrictedValues.includes(currentColValue);
  const oldColValRestricted = restrictedValues.includes(oldColValue);
  return currentColValRestricted || oldColValRestricted;
};

const getInitialRestrictedColValue = (
  initialSelectedRows: SelectedRows,
  rowIdToData: Record<string, TableRow>,
  restrictedColumn: RestrictedColumn
): string | undefined => {
  const selectedRowIds = Object.keys(initialSelectedRows);
  if (!rowIdToData || !selectedRowIds.length) return;

  const { columnKey: restrictedColKey, restrictedValues = [] } = restrictedColumn || {};
  let oldColValue: string | undefined;

  for (const rowId of selectedRowIds) {
    const rowData = rowIdToData[rowId];
    if (!rowData) {
      throw new Error(`Row ID "${rowId}" does not exist in the table data.`);
    }
    if (!restrictedColumn) {
      continue;
    }

    // Validate restricted columns Value.
    const colValue = getColumnValue(rowData[restrictedColKey], true);
    if (isRestrictedColValueInvalid(colValue, oldColValue, restrictedValues)) {
      throw new Error(
        `Column "${restrictedColKey}" is configured for single value selection, but initialSelectedRows have invalid values for this column.`
      );
    }
    oldColValue = colValue;
  }
  return oldColValue;
};

export const ContextSelector: React.FC<ContextSelectorProps> = ({
  tableInfo,
  type = ContextSelectorType.Modal,
  rowSelectionMode = listsV2.RowSelectionMode.Multiple,
  triggerText = 'Open context selector',
  tabs = [DEFAULT_TAB],
  selectionSectionHeader = 'Selected items',
  initialSelectedRows = {},
  restrictedColumn,
  onView,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<Tab>(tabs[0] || DEFAULT_TAB);
  const [selectedRows, setSelectedRows] = useState<SelectedRows>(initialSelectedRows);
  const [restrictedColData, setRestrictedColData] = useState<RestrictedColData>(restrictedColumn);

  // Map row IDs to row data for quick lookup
  const rowIdToData: Record<string, TableRow> = React.useMemo(
    () =>
      tableInfo.data.reduce((acc, row) => {
        acc[row.id] = row;
        return acc;
      }, {}),
    [tableInfo.data]
  );

  // Effect to validate initialSelectedRows and set initial restricted columns selectedValue.
  useEffect(() => {
    const initialRestrictedColVal = getInitialRestrictedColValue(initialSelectedRows, rowIdToData, restrictedColumn);
    // If restrictedColumn is provided, set selectedValue of setRestrictedColData.
    if (restrictedColumn && initialRestrictedColVal) {
      setRestrictedColData((prev): RestrictedColData => ({ ...prev, selectedValue: initialRestrictedColVal }));
    }
  }, [initialSelectedRows, rowIdToData, restrictedColumn]);

  // If restricted column provided and all selections are cleared, remove restrictedColData's selectedValue.
  useEffect(() => {
    if (!Object.keys(selectedRows).length && restrictedColumn) {
      setRestrictedColData(({ selectedValue, ...colDataWithoutSelectedValue }) => colDataWithoutSelectedValue);
    }
  }, [selectedRows, restrictedColumn]);

  const getContainerProps = useCallback(() => {
    // Container props based on type
    const modalProps: ContextSelectorContainerModalProps & { variant: ContextSelectorType.Modal } = {
      variant: ContextSelectorType.Modal,
      modalTrigger: (
        <Button onClick={() => setIsOpen(true)} mode="primary">
          {triggerText}
        </Button>
      ),
      isModalOpen: isOpen,
      onModalClose: () => {
        setIsOpen(false);
        // Reset selected rows to existing selections when closing modal.
        setSelectedRows(initialSelectedRows);
      },
    };
    const dropdownProps: ContextSelectorContainerDropdownProps & { variant: ContextSelectorType.Dropdown } = {
      variant: ContextSelectorType.Dropdown,
      label: triggerText,
      isOpen,
      onClick: () => {
        setIsOpen(prevIsOpen => {
          // If the dropdown is being opened, reset selected rows to existing selections.
          if (!prevIsOpen) {
            setSelectedRows(initialSelectedRows);
          }
          return !prevIsOpen;
        });
      },
      width: 'auto',
      size: 'md',
      hoverColor: 'light-orange' as BackgroundColor,
    };
    return type === ContextSelectorType.Modal ? modalProps : dropdownProps;
  }, [type, initialSelectedRows, isOpen, triggerText]);

  // Handlers
  const handleTabChange = useCallback((tab: Tab) => setSelectedTab(tab), []);

  const handleSelectRows = useCallback(
    (rows: SelectedRows | (() => SelectedRows)) => {
      const selected = typeof rows === 'object' ? rows : (rows as () => SelectedRows)();
      const rowId = Object.keys(selected)?.[0];
      if (!restrictedColData || !rowId) {
        setSelectedRows(rows);
        return;
      }

      const { columnKey, selectedValue: existingColValue, restrictedValues = [] } = restrictedColData;
      const currentColValue = getColumnValue(rowIdToData[rowId]?.[columnKey], true);
      // If new row's restricted column's value different from existing value, update restrictedColData and remove all existing selected rows.
      if (
        currentColValue !== existingColValue &&
        (!restrictedValues.length || restrictedValues.includes(currentColValue))
      ) {
        setRestrictedColData(prev => ({ ...prev, selectedValue: currentColValue }));
        setSelectedRows({ [rowId]: true });
        return;
      }
      // If existing restricted column's value exists but new selection is not a restricted value, remove selectedValue & all existing selected rows.
      else if (existingColValue && restrictedValues.length && !restrictedValues.includes(currentColValue)) {
        setRestrictedColData(({ selectedValue, ...colDataWithoutSelectedValue }) => colDataWithoutSelectedValue);
        setSelectedRows({ [rowId]: true });
        return;
      }
      setSelectedRows(rows);
    },
    [rowIdToData, restrictedColData]
  );

  const handleChipDelete = useCallback((rowId: string) => {
    setSelectedRows((prev): SelectedRows => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });
  }, []);

  const handleView = useCallback(() => {
    const selectedData = Object.keys(selectedRows).map(rowId => rowIdToData[rowId]);
    onView(selectedData);
    setIsOpen(false);
  }, [selectedRows, rowIdToData, onView]);

  const handleOnCancel = useCallback(() => {
    setIsOpen(false);
    setSelectedRows(initialSelectedRows);
  }, [initialSelectedRows]);

  return (
    <ContextSelectorContainer
      {...getContainerProps()}
      contextNavigation={<ContextNavigation tabs={tabs} selectedTab={selectedTab} onTabChange={handleTabChange} />}
      contextBody={
        <ContextBody
          tableInfo={tableInfo}
          rowSelectionMode={rowSelectionMode}
          selectedRows={selectedRows}
          selectedTab={selectedTab}
          onRowSelection={handleSelectRows}
          restrictedColumn={restrictedColData}
        />
      }
      contextSelectionHeader={selectionSectionHeader}
      contextSelectionBody={
        <SelectionBody
          selectedRows={selectedRows}
          rowSelectionMode={rowSelectionMode}
          onChipDelete={handleChipDelete}
          rowIdToData={rowIdToData}
        />
      }
      contextSelectionFooter={
        <SelectionFooter
          selectedRows={selectedRows}
          rowSelectionMode={rowSelectionMode}
          onClearAll={() => setSelectedRows({})}
          onCancel={handleOnCancel}
          onView={handleView}
        />
      }
      testId="context-selector-container"
    />
  );
};
